import { signRequestWithKey } from '../crypto/signing';
import { upsertPushContext } from '../crypto/storage';
import type { SignedRequest } from '@shared/types';
import type { SigningKey } from '../crypto/ed25519Provider';
import { useAuthStore } from '../store/authStore';
import {
  canUseNativeMobilePushRuntime,
  isWebViewMode,
  refreshNativeMobilePushRegistration
} from '../utils/webviewBridge';

export type PushDeliveryMode = 'relay';

const VAPID_KEY_CACHE_KEY = 'videocall.web_push.vapid_public_key.v1';

function normalizeUrlPrefix(url: string): string {
  return (url || '').trim().replace(/\/+$/, '');
}

function getApiPrefixForDevice(deviceId: string): string {
  // No proxy on the client origin.
  // Push endpoints must always be called on the tenant server.
  const state = useAuthStore.getState();
  const device = state.devices[deviceId];
  if (!device) {
    throw new Error('Missing device in store (cannot determine server URL for push)');
  }

  const serverUrl = state.identities[device.identityId]?.serverUrl;
  if (!serverUrl) {
    throw new Error('Missing identity serverUrl (cannot determine server URL for push)');
  }

  return `${normalizeUrlPrefix(serverUrl)}/api`;
}

function getServerIdForDevice(deviceId: string): string | null {
  const state = useAuthStore.getState();
  const device = state.devices[deviceId];
  if (!device) return null;
  return state.identities[device.identityId]?.serverId || null;
}

function getServerUrlForDevice(deviceId: string): string | null {
  const state = useAuthStore.getState();
  const device = state.devices[deviceId];
  if (!device) return null;
  return state.identities[device.identityId]?.serverUrl || null;
}

function getServerMetaForDevice(deviceId: string): { serverId: string | null; serverUrl: string | null; serverName: string | null } {
  const state = useAuthStore.getState();
  const device = state.devices[deviceId];
  if (!device) return { serverId: null, serverUrl: null, serverName: null };
  const identity = state.identities[device.identityId];
  return {
    serverId: identity?.serverId || null,
    serverUrl: identity?.serverUrl || null,
    serverName: identity?.serverName || null
  };
}


/**
 * Convert VAPID public key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function ensureLocalPushSubscription(
  publicKey: string,
  opts?: { forceResubscribe?: boolean }
): Promise<PushSubscription | null> {
  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration) return null;

    const applicationServerKeyBytes = urlBase64ToUint8Array(publicKey);
    const applicationServerKey = (applicationServerKeyBytes.buffer as ArrayBuffer).slice(
      applicationServerKeyBytes.byteOffset,
      applicationServerKeyBytes.byteOffset + applicationServerKeyBytes.byteLength
    );

    let subscription = await registration.pushManager.getSubscription();

    // If we already have a subscription, but it was created with a different VAPID public key,
    // delivery may fail (e.g. 401/403 from the push endpoint). Best effort: auto-resubscribe.
    let keyMismatch = false;
    const existingServerKey = (subscription as any)?.options?.applicationServerKey as ArrayBuffer | null | undefined;
    if (subscription && existingServerKey) {
      const a = new Uint8Array(existingServerKey);
      const b = new Uint8Array(applicationServerKey);
      const same = a.length === b.length && a.every((v, i) => v === b[i]);
      keyMismatch = !same;
    }

    const shouldResubscribe = !!opts?.forceResubscribe || keyMismatch;
    if (subscription && shouldResubscribe) {
      try {
        await subscription.unsubscribe();
      } catch {
        // ignore
      }
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
    }

    return subscription;
  } catch (error) {
    console.error('[Push] ensureLocalPushSubscription failed', error);
    return null;
  }
}

function serializeSubscription(subscription: PushSubscription) {
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
      auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!)))
    }
  };
}

async function registerPushSubscriptionOnServer(params: {
  deviceId: string;
  deviceSigningKey: SigningKey;
  subscription: PushSubscription;
}): Promise<boolean> {
  const { deviceId, deviceSigningKey, subscription } = params;
  try {
    const signedRequest: SignedRequest<{
      subscription: ReturnType<typeof serializeSubscription>;
    }> = await signRequestWithKey(
      'push:subscribe',
      { subscription: serializeSubscription(subscription) },
      deviceId,
      deviceSigningKey
    );

    const response = await fetch(`${getApiPrefixForDevice(deviceId)}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(signedRequest)
    });

    const data = await response.json();
    if (data.success) {
      if (data.pushId) {
        const meta = getServerMetaForDevice(deviceId);
        await upsertPushContext({
          pushId: String(data.pushId),
          serverOrigin: meta.serverUrl,
          serverId: meta.serverId,
          serverUrl: meta.serverUrl,
          serverName: meta.serverName
        });
      }
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

async function unregisterPushSubscriptionOnServer(params: {
  deviceId: string;
  deviceSigningKey: SigningKey;
  endpoint: string;
}): Promise<boolean> {
  const { deviceId, deviceSigningKey, endpoint } = params;
  try {
    const signedRequest: SignedRequest<{ endpoint: string }> = await signRequestWithKey(
      'push:unsubscribe',
      { endpoint },
      deviceId,
      deviceSigningKey
    );

    await fetch(`${getApiPrefixForDevice(deviceId)}/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(signedRequest)
    });

    return true;
  } catch {
    return false;
  }
}

/**
 * Register Service Worker
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    // Prefer an existing registration (main.tsx registers a cache-busted sw.js?v=BUILD).
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) {
      await navigator.serviceWorker.ready;
      return existing;
    }

    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Get VAPID public key from server
 */
async function fetchPushVapidKey(deviceId: string): Promise<string | null> {
  try {
    const url = new URL(`${getApiPrefixForDevice(deviceId)}/push/vapid-key`);

    const response = await fetch(url.toString(), { cache: 'no-store' });
    const data = await response.json();

    if (data.success) {
      return String(data.publicKey || '');
    }

    console.error('Failed to get VAPID public key:', data.error);
    return null;
  } catch (error) {
    console.error('Error fetching VAPID public key:', error);
    return null;
  }
}

type PushEnableResult = {
  ok: boolean;
  appliedServerIds: string[];
  failedServerIds: string[];
  reason?: string;
  warning?: string;
};

function choosePreferredVapidKey(params: {
  fetchedKeys: Array<{ deviceId: string; serverId: string; publicKey: string }>;
  currentDeviceId: string | null;
}): { publicKey: string; warning?: string } {
  const groups = new Map<string, Array<{ deviceId: string; serverId: string; publicKey: string }>>();
  for (const entry of params.fetchedKeys) {
    const bucket = groups.get(entry.publicKey) || [];
    bucket.push(entry);
    groups.set(entry.publicKey, bucket);
  }

  const ranked = Array.from(groups.entries())
    .map(([publicKey, entries]) => ({
      publicKey,
      entries,
      count: entries.length,
      hasCurrentDevice: entries.some((entry) => entry.deviceId === params.currentDeviceId),
    }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      if (a.hasCurrentDevice !== b.hasCurrentDevice) return a.hasCurrentDevice ? -1 : 1;
      return a.publicKey.localeCompare(b.publicKey);
    });

  const winner = ranked[0]!;
  if (ranked.length === 1) {
    return { publicKey: winner.publicKey };
  }

  const losers = ranked
    .slice(1)
    .flatMap((group) => group.entries.map((entry) => entry.serverId));

  return {
    publicKey: winner.publicKey,
    warning: `VAPID keys differ across connected servers. Using the key from: ${winner.entries.map((entry) => entry.serverId).join(', ')}. Check: ${Array.from(new Set(losers)).join(', ')}`
  };
}

export async function enablePushNotificationsOnAllKnownServers(): Promise<PushEnableResult> {
  if (isWebViewMode) {
    const ok = refreshNativeMobilePushRegistration();
    return {
      ok,
      appliedServerIds: ok ? ['native-mobile-push'] : [],
      failedServerIds: ok ? [] : ['native-mobile-push'],
      reason: ok ? undefined : 'Native mobile push runtime is not available'
    };
  }

  if (!isPushSupported()) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Push not supported' };
  }

  if (!('Notification' in window)) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Notifications not supported' };
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Permission not granted' };
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Service Worker not available' };
  }

  const state = useAuthStore.getState();
  const devices = Object.values(state.devices).filter((d) => !!d.deviceId && !!d.signingKey);
  if (devices.length === 0) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'No devices available' };
  }

  const currentDeviceId = state.currentDeviceId || null;
  const prioritizedDevices = [
    ...devices.filter((d) => d.deviceId === currentDeviceId),
    ...devices.filter((d) => d.deviceId !== currentDeviceId)
  ];
  const uniqueDevicesByServer = prioritizedDevices.filter((device, index, arr) => {
    const serverUrl = getServerUrlForDevice(device.deviceId) || '';
    return arr.findIndex((candidate) => (getServerUrlForDevice(candidate.deviceId) || '') === serverUrl) === index;
  });

  const fetchedKeys: Array<{ deviceId: string; serverId: string; publicKey: string }> = [];
  for (const device of uniqueDevicesByServer) {
    const publicKey = await fetchPushVapidKey(device.deviceId);
    if (!publicKey) continue;
    fetchedKeys.push({
      deviceId: device.deviceId,
      serverId: getServerIdForDevice(device.deviceId) || device.deviceId,
      publicKey
    });
  }

  if (fetchedKeys.length === 0) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Push not configured on any connected server' };
  }

  const selectedKey = choosePreferredVapidKey({ fetchedKeys, currentDeviceId });
  const publicKey = selectedKey.publicKey;

  let forceResubscribe = false;
  try {
    const cached = String(localStorage.getItem(VAPID_KEY_CACHE_KEY) || '');
    forceResubscribe = !!cached && cached !== publicKey;
  } catch {
    // ignore
  }

  const subscription = await ensureLocalPushSubscription(publicKey, { forceResubscribe });
  if (!subscription) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Failed to create push subscription' };
  }

  try {
    localStorage.setItem(VAPID_KEY_CACHE_KEY, publicKey);
  } catch {
    // ignore
  }

  const appliedServerIds: string[] = [];
  const failedServerIds: string[] = [];

  for (const d of devices) {
    const deviceId = d.deviceId!;
    const ok = await registerPushSubscriptionOnServer({
      deviceId,
      deviceSigningKey: d.signingKey,
      subscription
    });

    const serverId = getServerIdForDevice(deviceId) || deviceId;
    if (ok) appliedServerIds.push(serverId);
    else failedServerIds.push(serverId);
  }

  return {
    ok: appliedServerIds.length > 0,
    appliedServerIds,
    failedServerIds,
    warning: selectedKey.warning
  };
}

export async function resetPushNotificationsOnAllKnownServers(): Promise<PushEnableResult> {
  if (isWebViewMode) {
    return enablePushNotificationsOnAllKnownServers();
  }

  if (!isPushSupported()) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Push not supported' };
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    return { ok: false, appliedServerIds: [], failedServerIds: [], reason: 'Service Worker not available' };
  }

  const existingSubscription = await registration.pushManager.getSubscription().catch(() => null);
  const existingEndpoint = existingSubscription?.endpoint || '';

  const state = useAuthStore.getState();
  const devices = Object.values(state.devices).filter((d) => !!d.deviceId && !!d.signingKey);

  const failedServerIds: string[] = [];
  if (existingEndpoint) {
    for (const device of devices) {
      const ok = await unregisterPushSubscriptionOnServer({
        deviceId: device.deviceId,
        deviceSigningKey: device.signingKey,
        endpoint: existingEndpoint
      });
      if (!ok) {
        failedServerIds.push(getServerIdForDevice(device.deviceId) || device.deviceId);
      }
    }
  }

  if (existingSubscription) {
    try {
      await existingSubscription.unsubscribe();
    } catch {
      // ignore unsubscribe failures and continue with re-subscribe
    }
  }

  try {
    localStorage.removeItem(VAPID_KEY_CACHE_KEY);
  } catch {
    // ignore
  }

  const result = await enablePushNotificationsOnAllKnownServers();
  return {
    ok: result.ok,
    appliedServerIds: result.appliedServerIds,
    failedServerIds: Array.from(new Set([...failedServerIds, ...result.failedServerIds])),
    reason: result.reason,
    warning: result.warning
  };
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  if (isWebViewMode) return canUseNativeMobilePushRuntime();
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Request notification permission
 */
async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

// Delivery selection is intentionally removed: the client always subscribes using the relay VAPID key.
